/**
 * Sector times in a race (Al Kamel timing: SRO, GR Cup, IMSA): each lap's S1–S3 (s) and top speed
 * (mph) as the import keeps them (server csv-utils readSectors). Bests and the Sectors view count
 * green racing laps only (gap-math isCleanLap: green, not a pit or out-lap, under the race's
 * green-pace cutoff), so a lap behind the safety car or into the pits never sets a best.
 */
import type {
  LapData,
  QualifyingCar,
  QualifyingChartData,
  QualifyingLap,
  RaceChartData,
} from "@shared/types";
import { carNo } from "./car-number";
import { isCleanLap } from "./gap-math";

export const SECTORS = ["s1", "s2", "s3"] as const;
export type SectorKey = (typeof SECTORS)[number];

/** All three sectors timed on this lap. */
export const hasAllSectors = (l: LapData): l is LapData & Record<SectorKey, number> =>
  !!(l.s1 && l.s2 && l.s3);

const anySectors = new WeakMap<RaceChartData, boolean>();
/** The race's timing gives sector times (some lap has all three). */
export function hasSectors(data: RaceChartData): boolean {
  let v = anySectors.get(data);
  if (v === undefined) {
    v = Object.values(data.cars).some((c) => c.laps.some(hasAllSectors));
    anySectors.set(data, v);
  }
  return v;
}

/** The green racing laps with all three sectors, by chart key. */
const racingLaps = new WeakMap<RaceChartData, Map<string, LapData[]>>();
export function sectorLaps(data: RaceChartData): Map<string, LapData[]> {
  let m = racingLaps.get(data);
  if (!m) {
    m = new Map();
    for (const [k, car] of Object.entries(data.cars)) {
      m.set(
        k,
        car.laps.filter((l) => hasAllSectors(l) && isCleanLap(data, car, l.l))
      );
    }
    racingLaps.set(data, m);
  }
  return m;
}

export type SectorBests = Record<SectorKey, number | null>;

/** The quickest of each sector over these cars' green racing laps. */
export function sectorBests(data: RaceChartData, keys: string[]): SectorBests {
  const laps = sectorLaps(data);
  const out: SectorBests = { s1: null, s2: null, s3: null };
  for (const k of keys) {
    for (const l of laps.get(k) ?? []) {
      for (const s of SECTORS) {
        const v = l[s]!;
        if (out[s] === null || v < out[s]!) out[s] = v;
      }
    }
  }
  return out;
}

/** A car's best sectors, and its class's. */
export function lapSectorContext(
  data: RaceChartData,
  focusNum: number
): { car: SectorBests; cls: SectorBests } | null {
  const car = data.cars[String(focusNum)];
  if (!car || !hasSectors(data)) return null;
  const classKeys = Object.entries(data.cars)
    .filter(([, c]) => c.cls === car.cls)
    .map(([k]) => k);
  return {
    car: sectorBests(data, [String(focusNum)]),
    cls: sectorBests(data, classKeys),
  };
}

/** Seconds as the timing prints a sector: "43.680", "1:28.398". */
export function sectorText(s: number | null | undefined): string {
  if (!(typeof s === "number" && s > 0)) return "—";
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return m ? `${m}:${r.toFixed(3).padStart(6, "0")}` : r.toFixed(3);
}

/**
 * The race's green racing laps with sectors in the qualifying stats shape (StatsTable): one row per
 * car with any, its number as published, its team, its best lap and best sectors from those laps.
 */
export function sectorStatsData(data: RaceChartData, name: string): QualifyingChartData {
  const laps = sectorLaps(data);
  const cars: QualifyingCar[] = [];
  let total = 0;
  for (const [k, car] of Object.entries(data.cars)) {
    const mine = laps.get(k) ?? [];
    if (!mine.length) continue;
    total += mine.length;
    const ql: QualifyingLap[] = mine.map((l) => ({
      l: l.l,
      lt: l.lt,
      ltSec: l.ltSec,
      s1: l.s1!,
      s2: l.s2!,
      s3: l.s3!,
      s1f: sectorText(l.s1),
      s2f: sectorText(l.s2),
      s3f: sectorText(l.s3),
      s1imp: 0,
      s2imp: 0,
      s3imp: 0,
      flag: "GF",
      pit: false,
      lapImp: 0,
    }));
    const best = ql.reduce((b, l) => (l.ltSec < b.ltSec ? l : b), ql[0]);
    const b = sectorBests(data, [k]);
    cars.push({
      num: carNo(data, k),
      team: car.team,
      driver: car.team,
      cls: car.cls,
      mfr: car.make ?? "",
      laps: ql,
      bestLap: best.ltSec,
      bestLapNum: best.l,
      theoreticalBest: (b.s1 ?? 0) + (b.s2 ?? 0) + (b.s3 ?? 0),
      bestS1: b.s1 ?? 0,
      bestS2: b.s2 ?? 0,
      bestS3: b.s3 ?? 0,
    });
  }
  return {
    sessionName: name,
    track: "",
    series: "",
    date: "",
    cars,
    classes: [...new Set(cars.map((c) => c.cls))].sort(),
    totalLaps: total,
  };
}
